/**
 * The shared preview loop for the Documents & emails studios: refetch
 * whenever `previewKey` changes — immediately on the first render, debounced
 * (PREVIEW_DEBOUNCE_MS) after edits — always with the CURRENT values, so the
 * rendered artifact is the explanation. The previous render stays mounted,
 * dimmed, while the next one builds.
 */
import { useEffect, useRef, useState } from "react";
import type {
  DocumentPreviewRequest,
  DocumentPreviewResponse,
} from "../../lib/document-preview-api";

export const PREVIEW_DEBOUNCE_MS = 400;

/**
 * The document studios' preview key: the request that will be sent, so what
 * is keyed is always what is fetched. Deriving the key separately is what
 * froze the quote studio — an invalid side-field draft held the key still
 * while the payload moved on, and the failing request and its correction
 * hashed the same.
 *
 * The Template name is the one exclusion: it renders on the twin, but
 * refetching mid-rename would replace the sheet under the label being edited.
 */
export function previewKeyOf(request: DocumentPreviewRequest): string {
  const {
    quote_template_name: _quote,
    jobsheet_template_name: _jobsheet,
    ...keyed
  } = request;
  return JSON.stringify(keyed);
}

export function usePreview(
  previewKey: string,
  loadPreview: (signal: AbortSignal) => Promise<DocumentPreviewResponse>,
) {
  const [result, setResult] = useState<DocumentPreviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadRef = useRef(loadPreview);
  loadRef.current = loadPreview;
  const firstRun = useRef(true);

  useEffect(() => {
    const ac = new AbortController();
    const run = async () => {
      setLoading(true);
      try {
        const next = await loadRef.current(ac.signal);
        if (ac.signal.aborted) return;
        setResult(next);
        setError(null);
      } catch (err) {
        if (ac.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    };

    if (firstRun.current) {
      firstRun.current = false;
      void run();
      return () => ac.abort();
    }
    const timer = setTimeout(() => void run(), PREVIEW_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [previewKey]);

  return { result, loading, error };
}
